import React, { useState, useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import FileViewer from 'molecules/FileViewer/FileViewer'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'
import { useGenerateArtifact } from '../../api'
import { toast } from 'atoms/Toast'

export interface ArtifactEditorProps {
  disabled?: boolean
}

const ArtifactEditor: React.FC<ArtifactEditorProps> = ({ disabled = false }) => {
  const {
    translationMap,
    currentArtifactValue,
    updateArtifactValue,
    updateArtifactValueFromServer,
    isDirty,
    clearPendingChanges,
  } = useTranslationMapDetailsContext()

  // Local state for smooth typing experience
  const [localArtifactValue, setLocalArtifactValue] = useState(currentArtifactValue)
  const [fileViewerKey, setFileViewerKey] = useState(0)
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Generate Artifact API hook
  const {
    mutate: generateArtifact,
    isLoading: isGeneratingArtifact,
    error: generateArtifactError,
  } = useGenerateArtifact()

  // Sync local state with context when it changes
  useEffect(() => {
    setLocalArtifactValue(currentArtifactValue)
  }, [currentArtifactValue])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [])

  const handleArtifactChange = (newValue: string) => {
    // Update local state immediately for smooth typing
    setLocalArtifactValue(newValue)

    // Clear previous timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    // Debounce context update to prevent re-renders during typing
    updateTimeoutRef.current = setTimeout(() => {
      updateArtifactValue(newValue)
    }, 500)
  }

  const handleGenerateArtifact = () => {
    if (!translationMap?.id) {
      toast.error('Translation map ID is required to generate artifact')
      return
    }

    generateArtifact(translationMap.id, {
      onSuccess: updatedTranslationMap => {
        toast.success('Artifact generated successfully')

        // Handle 204 No Content response - check if we have encoded_implementation
        if (updatedTranslationMap === null) {
          // The query invalidation will trigger a refetch and the effect will handle the rest
          return
        }

        // Handle 204 response with encoded_implementation data
        if (
          updatedTranslationMap &&
          updatedTranslationMap.encoded_implementation &&
          !updatedTranslationMap.id
        ) {
          try {
            const decoded = atob(updatedTranslationMap.encoded_implementation)

            // Update local state and notify parent component
            setLocalArtifactValue(decoded)
            updateArtifactValueFromServer(decoded)

            // Force a re-render by updating the key
            setFileViewerKey(prev => prev + 1)

            return
          } catch (error) {
            console.error('Error decoding generated implementation from 204 response:', error)
          }
        }

        // Update the local artifact value with the newly generated content
        if (updatedTranslationMap?.encoded_implementation) {
          try {
            const decoded = atob(updatedTranslationMap.encoded_implementation)

            // Update local state and notify parent component
            setLocalArtifactValue(decoded)
            updateArtifactValueFromServer(decoded)

            // Force a re-render by updating the key
            setFileViewerKey(prev => prev + 1)
          } catch (error) {
            console.error('Error decoding generated implementation:', error)
          }
        } else {
          console.warn('No encoded_implementation in generated artifact response')
        }

        // The API hook will automatically invalidate queries and refetch data
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to generate artifact'
        toast.error(errorMessage)
      },
    })
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '400px',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, primary.main, primary.light)',
          opacity: 0.6,
          zIndex: 1,
        },
      }}
    >
      <FileViewer
        key={`artifact-${translationMap?.id}-${fileViewerKey}`}
        content={localArtifactValue}
        contentType="python"
        showOptions={false}
        options={{ heigth: '530px', readonly: disabled }}
        onChange={handleArtifactChange}
      />
    </Box>
  )
}

export default ArtifactEditor
